import { ChangeDetectionStrategy, Component, computed, effect, inject, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CreateUserDialogStore } from './create-user-dialog.store';
import { isInvalid } from '../../../../shared/helpers/validate';
import { CreateUserInput, UserGroupUpsertInput, UserRuleUpsertInput, WorkplaceObject } from '@it2go/types';
import { EntityEventMessengerEnum } from '../../../../../../../../types/src/common/enum/entity-event-messenger.enum';
import { onEventMarker } from '../../../../../store/global/event';
import { Router } from '@angular/router';
import { UserSexEnum, userSexToEnumOptions } from '@libs/types/src/user/enum/user-sex.enum';
import { WorkloadTypeEnum, workloadTypeToEnumOptions } from '@libs/types/src/user/enum/workload-type.enum';
import {
    CreateEmploymentInput,
    EmploymentWorkplaceInput,
} from '@libs/types/src/user/input-type/create-employment.input';
import {
    parseRuleId,
} from '../../../../../components/user-management/user-detail/user-role/select-user-rule/select-user-rule.store';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { contractVisibility } from '../shared/helpers';
import { contentGlobalErrors, contentGlobalSuccess } from '../../../content.error';
import { userUri } from '../../../../../utils/router.utils';
import { changeSignal } from '../../../../../utils/form.utils';
import { EmploymentEnum } from '@libs/types/src/user/enum/employment.enum';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import {
    ContractCascadeSelectObject,
} from 'packages/client/src/app/components/kit/input/cascade-contract-select-input/cascade-contract-select-input.types';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import {
    CascadeContractSelectInputComponent,
} from '../../../../../components/kit/input/cascade-contract-select-input/cascade-contract-select-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import {
    EmploymentTypeSelectInputComponent,
} from '../../../../../components/kit/input/employment-type-select-input/employment-type-select-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { ErrorFloatMessageComponent } from '../../../../../components/kit/float-message/error-float-message.component';
import { NumberInputComponent } from '../../../../../components/kit/input/text-input/number-input.component';
import { PasswordInputComponent } from '../../../../../components/kit/input/password-input/password-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import {
    SelectUserGroupComponent,
} from '../../../../../components/user-management/user-detail/user-role/select-user-group/select-user-group.component';
import {
    SelectUserRuleComponent,
} from '../../../../../components/user-management/user-detail/user-role/select-user-rule/select-user-rule.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { WarnFloatMessageComponent } from '../../../../../components/kit/float-message/warn-float-message.component';

@Component({
    selector: 'app-create-user-dialog',
    templateUrl: './create-user-dialog.component.html',
    providers: [CreateUserDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        CascadeContractSelectInputComponent,
        DateInputComponent,
        EmailInputComponent,
        EmploymentTypeSelectInputComponent,
        ErrorFloatMessageComponent,
        NumberInputComponent,
        PasswordInputComponent,
        ReactiveFormsModule,
        SelectInputComponent,
        SelectUserGroupComponent,
        SelectUserRuleComponent,
        TextInputComponent,
        WarnFloatMessageComponent,
    ],
})
export class CreateUserDialogComponent extends BaseDialogComponent {

    protected readonly store = inject(CreateUserDialogStore);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);

    protected workloadVisible = false;
    protected scopeOfWorkVisible = false;
    protected loading = false;
    protected serviceId = 0;
    protected lastServiceId = 0;

    protected workplaces: WorkplaceObject[] = [];

    protected withServices = computed(() => this.store.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS)());

    protected form = this.fb.group({
        id: this.fb.control<string | null>(null),
        name: this.fb.control<string | null>(null, [Validators.required]),
        surname: this.fb.control<string | null>(null, [Validators.required]),
        email: this.fb.control<string | null>(null, [Validators.required, Validators.email]),
        password: this.fb.control<string | null>(null, [Validators.required]),
        sex: this.fb.control<UserSexEnum | null>(UserSexEnum.male, [Validators.required]),
        groupIds: this.fb.control<number[] | null>([]),
        ruleIds: this.fb.control<string[] | null>([]),
        service: this.fb.control<number | null>(null, [Validators.required]),
        contract: this.fb.control<ContractCascadeSelectObject | null>(null, [Validators.required]),
        dateFrom: this.fb.control<Date>(new Date(), [Validators.required]),
        workplace: this.fb.control<number | null>(null),
        workloadType: this.fb.control<WorkloadTypeEnum | null>(null),
        workloadSize: this.fb.control<number | null>(null),
        scopeOfWork: this.fb.control<number | null>(null),
        employmentTypeName: this.fb.control<string | null>(null, [Validators.required]),
    });

    protected sexOptions = userSexToEnumOptions;
    protected emailChange = toSignal(this.form.controls.email.valueChanges);
    protected contractChange = changeSignal(this.form.controls.contract);
    protected serviceIdChange = changeSignal(this.form.controls.service);

    protected staticFields = [
        'name', 'surname', 'password', 'sex',
    ];

    protected checks = computed(() => {
        const user = this.store.user();
        const organization = user?.organizations
            ?.find((it) => it.id == this.store.globalGlobalStore.organizationStore.selectedId());

        // TODO tohle se předpokládám dojebe např. u Mado, až tam nebude services microslužba -_-
        const serviceId = this.serviceIdChange();
        const employment = !serviceId || user?.employments
            ?.find((it) => it.serviceId === serviceId);

        const toEmploy = !!user && !!organization && !employment && this.withServices() && this.store.isAllowed('service.read')();

        return {
            exists: !!user,
            toAssign: !!user && !organization,
            toEmploy,
        };
    });

    constructor() {
        super();

        effect(() => {
            const user = this.store.user();

            untracked(() => {
                if (user) {
                    this.form.patchValue({
                        id: user.id,
                        name: user.name,
                        surname: user.surname,
                        sex: user.sex as any,
                    }, { emitEvent: false });

                    this.staticFields.forEach((it) => {
                        const control = (this.form.controls as any)[it];
                        control.clearValidators();
                        control.disable({ emitEvent: false });
                        control.updateValueAndValidity({ emitEvent: false });
                    });
                } else {
                    this.staticFields.forEach((it) => {
                        const control = (this.form.controls as any)[it];
                        control.enable({ emitEvent: false });
                        control.setValidators(Validators.required);
                        control.updateValueAndValidity({ emitEvent: false });
                    });
                }
            });
        });

        effect(() => {
            const { toEmploy } = this.checks();

            untracked(() => {
                if (toEmploy) {
                    this.form.controls.ruleIds.disable({ emitEvent: false });
                    this.form.controls.groupIds.disable({ emitEvent: false });
                } else {
                    this.form.controls.ruleIds.enable({ emitEvent: false });
                    this.form.controls.groupIds.enable({ emitEvent: false });
                }
            });
        });

        effect(() => {
            if (this.withServices()) {
                this.form.controls.service.setValidators(Validators.required);
                this.form.controls.contract.setValidators(Validators.required);
                this.form.controls.employmentTypeName.setValidators(Validators.required);
            } else {
                this.form.controls.service.clearValidators();
                this.form.controls.contract.clearValidators();
                this.form.controls.employmentTypeName.clearValidators();
            }
            this.form.controls.service.updateValueAndValidity({ emitEvent: false });
            this.form.controls.contract.updateValueAndValidity({ emitEvent: false });
            this.form.controls.employmentTypeName.updateValueAndValidity({ emitEvent: false });
        });

        effect(() => {
            this.emailChange() && this.store.searchUser.next(this.emailChange()!);
        });

        effect(() => {
            const contract = this.contractChange();
            const visibility = contractVisibility(contract?.value);
            this.workloadVisible = visibility.workloadVisible;
            this.scopeOfWorkVisible = visibility.scopeOfWorkVisible;
            this.form.patchValue({ workloadSize: null, scopeOfWork: null }, { emitEvent: false });
        });
        this.store.onSuccessOrFail(
            () => {
                this.loading = false;
            },
            'createUser',
            'giveAccess',
        );
    }

    public async saveUser(): Promise<void> {
        if (isInvalid(this.form)) return;
        this.loading = true;
        const organizationId = this.store.globalGlobalStore.organizationStore.selectedId();
        let userId = this.form.value.id;

        const created = onEventMarker(EntityEventMessengerEnum.ORGANIZATION_USER_UPSERT);
        const checks = this.checks();

        if (checks.toAssign) {
            const result = await this.store.api.giveAccess({ userId: this.form.value.id!, organizationId });
            if (!result?.ok) {
                contentGlobalErrors.next('user.createUser.giveAccessFailed');
                this.loading = false;
                return;
            }
        } else if (!checks.exists) {
            const groupData = '{}';
            const user: CreateUserInput = {
                name: this.form.value.name ?? '',
                surname: this.form.value.surname ?? '',
                email: this.form.value.email ?? '',
                password: this.form.value.password ?? '',
                sex: this.form.value.sex ?? UserSexEnum.male,
                address: null,
                titleAfterName: null,
                titleBeforeName: null,
                phone: null,
                mobile: null,
                icpOfDoctor: null,
                doctor: this.form.value.employmentTypeName == EmploymentEnum.DOCTOR,
                organizationId,
                groups: this.form.value.groupIds?.map((it) => (<UserGroupUpsertInput>{
                    id: it,
                    data: groupData,
                })) ?? [],
                rules: this.form.value.ruleIds?.map((rule: string) => ({
                    ...parseRuleId(rule),
                    data: groupData,
                }) as UserRuleUpsertInput) ?? [],
            };

            const userRes = await this.store.api.createUser(user);
            userId = userRes.id;
        }

        // Assign rules & roles
        if (checks.toAssign) {
            const groupData = '{}';
            await this.store.api.updateUser({
                id: userId!,
                organizationId,
                groups: this.form.value.groupIds?.map((it) => (<UserGroupUpsertInput>{
                    id: it,
                    data: groupData,
                })) ?? [],
                rules: this.form.value.ruleIds?.map((rule: string) => ({
                    ...parseRuleId(rule),
                    data: groupData,
                }) as UserRuleUpsertInput) ?? [],
            });
        }

        if (this.form.value.employmentTypeName || this.form.value.service) {
            const employment: CreateEmploymentInput = {
                dateFrom: this.form.value.dateFrom!.toISOString(),
                dateTo: null,
                serviceId: this.form.value.service!,
                workplaces: this.form.value.workplace ? [{ workplaceId: this.form.value.workplace }] as EmploymentWorkplaceInput[] : [],
                contract: this.form.value.contract!.value,
                workloadSize: this.form.value.workloadSize ?? null,
                workloadType: this.form.value.workloadType ?? null,
                employmentTypeName: this.form.value.employmentTypeName!,
                userId: userId!,
                scopeOfWork: this.form.value.scopeOfWork ?? null,
            };
            await this.store.api.createEmployment(employment);
        }

        await created;
        contentGlobalSuccess.next('common.created');
        this.router.navigateByUrl(userUri(userId!)).then();
        this.dialogRef.close(true);
    }

    onContractSelected($event: any) {
        this.form.patchValue({ service: $event?.value?.parentId, workloadType: null });
        this.serviceId = $event?.value?.parentId;
        this.workplaces = $event?.value?.workplaces;
        if (this.serviceId != this.lastServiceId) {
            this.form.controls.workplace.setValue(null);
        }
        this.lastServiceId = $event?.value?.parentId;
    }

    protected readonly workloadTypeToEnumOptions = workloadTypeToEnumOptions;
}
